import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Button, Dropdown, Space, Tooltip, message as toast } from 'antd';
import {
  BellFilled,
  BellOutlined,
  DownOutlined,
  EditOutlined,
  SettingOutlined,
  SoundFilled,
  SoundOutlined,
} from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { refreshPendingCounts } from 'layouts/pending-counts';
import {
  AgentOption,
  SupportBox,
  SupportConfigView,
  SupportSummary,
  SupportTemplate,
  ThreadDetail,
  ThreadRow,
} from 'pages/support-shared/types';
import {
  playChime,
  readSoundPref,
  supportError,
  usePolling,
  useSupportMeta,
  writeSoundPref,
} from 'pages/support-shared/utils';
import { showDesktopNotice, useDesktopNotify } from 'pages/support-shared/desktop-notify';
import ThreadList, { ListFilter } from './thread-list';
import Conversation from './conversation';
import ProfilePanel from './profile-panel';
import StartModal from './start-modal';
import styles from './styles.module.scss';

const BOXES: SupportBox[] = ['waiting', 'mine', 'pending', 'resolved', 'all'];
const PAGE = 30;

/**
 * Hộp thư hỗ trợ: người dùng nhắn "HP Media Hỗ trợ" trong app → hiện ở đây;
 * ADMIN/MOD trả lời bằng tài khoản của mình, người dùng nhận tin từ tài khoản
 * hỗ trợ. Hỏi tin mới định kỳ (danh sách 8 giây, luồng đang mở 4 giây) vì
 * socket của app chỉ giữ một kết nối cho mỗi tài khoản.
 */
const SupportInboxPage = () => {
  const [params, setParams] = useSearchParams();
  const { userId: meId, isAdmin } = useCurrentRoles();
  const meta = useSupportMeta();

  const box = (BOXES.includes(params.get('box') as SupportBox) ? params.get('box') : 'waiting') as SupportBox;
  const selectedId = Number(params.get('t')) || undefined;
  const [filter, setFilterState] = useState<ListFilter>({ box, q: '' });
  const [limit, setLimit] = useState(PAGE);
  const [items, setItems] = useState<ThreadRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<SupportSummary | null>(null);
  const [config, setConfig] = useState<SupportConfigView | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [templates, setTemplates] = useState<SupportTemplate[]>([]);
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [sound, setSound] = useState(readSoundPref);
  const desktop = useDesktopNotify();
  const [now, setNow] = useState(Date.now());
  const listSeq = useRef(0);
  const inboxRef = useRef<HTMLDivElement>(null);
  const [inboxHeight, setInboxHeight] = useState<number>();

  // Hộp thư lấp đúng phần màn hình còn lại dưới tiêu đề (tiêu đề cao/thấp tuỳ
  // độ rộng vì mô tả xuống dòng) → ba cột cuộn riêng, trang không cuộn.
  useLayoutEffect(() => {
    const update = () => {
      const el = inboxRef.current;
      if (!el) return;
      const scroller = el.closest('main')?.parentElement;
      const top = el.getBoundingClientRect().top + (scroller?.scrollTop || 0);
      setInboxHeight(Math.max(520, Math.floor(window.innerHeight - top - 24)));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
    // config: tiêu đề/nút đổi chiều cao sau khi tải xong cấu hình.
  }, [config, configError]);

  // Hộp trên URL (F5 / gửi link giữ đúng hộp).
  useEffect(() => {
    setFilterState((prev) => (prev.box === box ? prev : { ...prev, box }));
  }, [box]);

  const setFilter = (next: ListFilter) => {
    setFilterState(next);
    setLimit(PAGE);
    if (next.box !== box) {
      const p = new URLSearchParams(params);
      p.set('box', next.box);
      setParams(p, { replace: true });
    }
  };

  const backToList = () => {
    const p = new URLSearchParams(params);
    p.delete('t');
    setParams(p, { replace: true });
  };

  const select = (id: number) => {
    const p = new URLSearchParams(params);
    p.set('t', String(id));
    setParams(p, { replace: true });
  };

  const loadList = useCallback(async () => {
    const seq = ++listSeq.current;
    try {
      const res: any = await AxiosUtils.get(API.supportThreads, {
        box: filter.box,
        q: filter.q || undefined,
        userClass: filter.userClass,
        category: filter.category,
        page: 1,
        limit,
      });
      if (seq !== listSeq.current) return; // bộ lọc đã đổi trong lúc chờ
      setItems(res.data?.data || []);
      setTotal(res.data?.metadata?.total || 0);
    } catch {
      // Lỗi tạm thời khi hỏi định kỳ: giữ danh sách cũ.
    } finally {
      if (seq === listSeq.current) setLoading(false);
    }
  }, [filter, limit]);

  const loadSummary = useCallback(async () => {
    try {
      const res: any = await AxiosUtils.get(API.supportSummary);
      const next: SupportSummary = res.data;
      // Âm báo + popup máy tính do SupportNotifier (gắn ở layout) lo cho mọi trang.
      setSummary(next);
    } catch {
      // bỏ qua
    }
  }, []);

  const loadTemplates = useCallback(() => {
    AxiosUtils.get(API.supportTemplates)
      .then((res: any) => setTemplates(res.data || []))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    AxiosUtils.get(API.supportConfig)
      .then((res: any) => setConfig(res.data))
      .catch((error) => setConfigError(supportError(error)));
    AxiosUtils.get(API.supportAgents)
      .then((res: any) => setAgents(res.data || []))
      .catch(() => undefined);
    loadTemplates();
    loadSummary();
  }, [loadTemplates, loadSummary]);

  useEffect(() => {
    setLoading(true);
    loadList();
  }, [loadList]);

  usePolling(loadList, 8000);
  usePolling(loadSummary, 10000);
  usePolling(loadTemplates, 120000);
  usePolling(() => setNow(Date.now()), 30000);

  // Số tin chờ trên tiêu đề tab trình duyệt.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = summary?.waiting ? `(${summary.waiting}) ${base}` : base;
    return () => {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [summary?.waiting]);

  const onChanged = useCallback(() => {
    loadList();
    loadSummary();
    refreshPendingCounts();
  }, [loadList, loadSummary]);

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    writeSoundPref(next);
    if (next) playChime();
  };

  const toggleDesktop = async () => {
    if (desktop.enabled) {
      desktop.disable();
      toast.info('Đã tắt thông báo máy tính trên trình duyệt này.');
      return;
    }
    const result = await desktop.enable();
    if (result === 'granted') {
      showDesktopNotice({
        title: 'Đã bật thông báo Hộp thư hỗ trợ',
        body: 'Có người nhắn “HP Media Hỗ trợ” là popup hiện ở góc màn hình, kể cả khi đang mở trang khác.',
        tag: 'support-test',
        silent: false,
        onClick: () => undefined,
      });
    } else if (result === 'denied') {
      toast.warning('Trình duyệt đang chặn thông báo. Bấm biểu tượng ổ khoá cạnh địa chỉ trang → Thông báo → Cho phép.');
    }
  };

  const desktopButton =
    desktop.permission === 'unsupported' ? null : (
      <Tooltip
        title={
          desktop.enabled
            ? 'Đang bật popup góc màn hình máy tính khi có tin mới — bấm để tắt'
            : desktop.permission === 'denied'
            ? 'Trình duyệt đang chặn thông báo: bấm ổ khoá cạnh địa chỉ trang → Thông báo → Cho phép'
            : 'Hiện popup nhỏ ở góc màn hình máy tính (logo HP, tên, tóm tắt tin) khi có người nhắn'
        }
      >
        <Space.Compact>
          <Button
            icon={desktop.enabled ? <BellFilled /> : <BellOutlined />}
            onClick={toggleDesktop}
            type={desktop.enabled ? 'default' : 'primary'}
            ghost={!desktop.enabled}
          >
            {desktop.enabled ? 'Thông báo máy tính: bật' : 'Bật thông báo máy tính'}
          </Button>
          <Dropdown
            trigger={['click']}
            menu={{
              selectable: true,
              selectedKeys: [desktop.scope],
              onClick: ({ key }) => desktop.setScope(key as 'all' | 'mine'),
              items: [
                { key: 'all', label: 'Báo mọi tin mới' },
                { key: 'mine', label: 'Chỉ tin của tôi và chưa ai nhận' },
              ],
            }}
          >
            <Button
              icon={<DownOutlined />}
              type={desktop.enabled ? 'default' : 'primary'}
              ghost={!desktop.enabled}
              aria-label="Chọn tin được báo"
            />
          </Dropdown>
        </Space.Compact>
      </Tooltip>
    );

  const header = (
    <Space wrap>
      {desktopButton}
      <Tooltip title={sound ? 'Tắt âm báo tin mới' : 'Bật âm báo tin mới'}>
        <Button icon={sound ? <SoundFilled /> : <SoundOutlined />} onClick={toggleSound} aria-label="Âm báo" />
      </Tooltip>
      <Link to="/support/manage">
        <Button icon={<SettingOutlined />}>Văn mẫu & cài đặt</Button>
      </Link>
      <Button type="primary" icon={<EditOutlined />} onClick={() => setStartOpen(true)} disabled={!config?.ready}>
        Nhắn mới
      </Button>
    </Space>
  );

  if (configError) {
    return (
      <div>
        <PageTitle title="Hộp thư hỗ trợ" extra={header} />
        <Alert type="error" showIcon message="Không mở được Hộp thư hỗ trợ" description={configError} />
      </div>
    );
  }

  if (config && !config.ready) {
    return (
      <div>
        <PageTitle title="Hộp thư hỗ trợ" />
        <div className={styles.notReady}>
          <Alert
            type="warning"
            showIcon
            message="Chưa khởi tạo tài khoản “HP Media Hỗ trợ”"
            description={
              isAdmin ? (
                <>
                  Tài khoản này là người mà người dùng nhắn tới trong app. Vào{' '}
                  <Link to="/support/manage?tab=settings">Văn mẫu & cài đặt → Cài đặt</Link> để khởi tạo (một lần).
                </>
              ) : (
                'Nhờ Admin vào Văn mẫu & cài đặt → Cài đặt để khởi tạo tài khoản hỗ trợ.'
              )
            }
          />
        </div>
      </div>
    );
  }

  const current = detail && detail.thread.id === selectedId ? detail : null;

  return (
    <div>
      <PageTitle title="Hộp thư hỗ trợ" extra={header} />
      <div
        className={`${styles.inbox} ${selectedId ? styles.inboxHasThread : ''}`}
        ref={inboxRef}
        style={inboxHeight ? { height: inboxHeight } : undefined}
      >
        <ThreadList
          filter={filter}
          onFilter={setFilter}
          summary={summary}
          meta={meta}
          items={items}
          total={total}
          loading={loading}
          selectedId={selectedId}
          onSelect={select}
          onLoadMore={() => setLimit((l) => l + PAGE)}
          now={now}
        />
        {selectedId ? (
          <Conversation
            key={selectedId}
            threadId={selectedId}
            meId={meId}
            meta={meta}
            agents={agents}
            templates={templates}
            signAgentName={!!config?.signAgentName}
            supportName={config?.account?.nickName || 'HP Media Hỗ trợ'}
            detail={current}
            onDetail={setDetail}
            onChanged={onChanged}
            onBack={backToList}
          />
        ) : (
          <div className={`${styles.column} ${styles.chat}`}>
            <div className={styles.chatEmpty}>
              <div>
                <p style={{ fontSize: 15, marginBottom: 6 }}>Chọn một cuộc trò chuyện ở bên trái.</p>
                <p style={{ margin: 0 }}>
                  Người dùng nhắn cho <strong>{config?.account?.nickName || 'HP Media Hỗ trợ'}</strong> trong mục Tin nhắn
                  của app; tin hiện ở đây trong vài giây.
                </p>
              </div>
            </div>
          </div>
        )}
        {selectedId ? <ProfilePanel detail={current} meId={meId} isAdmin={isAdmin} /> : <div className={styles.profile} />}
      </div>
      <StartModal open={startOpen} onClose={() => setStartOpen(false)} onStarted={(id) => { select(id); onChanged(); }} />
    </div>
  );
};

export default SupportInboxPage;
